import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useAuth } from "@itaas/core/auth/useAuth";
import { PatientFormModal } from "@/components/patients/PatientFormModal";
import { PageHeader } from "@/components/PageHeader";
import { usePageMeta } from "@/context/PageMetaContext";
import {
  consultationDetailPath,
  createConsultation,
  listConsultationsByPatient,
} from "@/core/clinical/clinical-store";
import { CONSULTATION_STATUS_LABELS } from "@/core/clinical/types";
import { formatInvoiceDateShort, formatMoney } from "@/core/pharmacy/format";
import { listValidatedInvoices } from "@/core/pharmacy/pharmacy-store";
import {
  deletePatient,
  getPatientById,
  syncPatientsFromApi,
  updatePatient,
} from "@/core/patients/patient-store";
import { patientFullName, patientGenderLabel, patientTypeLabel } from "@/core/patients/types";
import { canWritePatients } from "@/core/permissions";
import { routes } from "@/router/all_routes";

export function PatientDetailPage() {
  const auth = useAuth();
  const canWrite = canWritePatients(auth);
  const { patientId = "" } = useParams();
  const navigate = useNavigate();
  const [editOpen, setEditOpen] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    void syncPatientsFromApi().then(() => setRefreshKey((k) => k + 1));
  }, [patientId]);

  const patient = useMemo(() => {
    void refreshKey;
    return getPatientById(patientId);
  }, [patientId, refreshKey]);

  usePageMeta(
    patient
      ? {
          pageTitle: `Patient — ${patientFullName(patient)}`,
          breadcrumbLabel: patientFullName(patient),
        }
      : null,
  );

  const invoices = useMemo(() => {
    if (!patient) return [];
    const name = patientFullName(patient).toLowerCase();
    return listValidatedInvoices().filter(
      (inv) =>
        inv.patientId === patient.id ||
        inv.customer.name.trim().toLowerCase() === name,
    );
  }, [patient, refreshKey]);

  const consultations = useMemo(() => {
    if (!patient) return [];
    void refreshKey;
    return listConsultationsByPatient(patient.id);
  }, [patient, refreshKey]);

  if (!patient) {
    return (
      <div className="alert alert-warning">
        Patient introuvable.{" "}
        <Link to={routes.patients}>Retour à la liste</Link>
      </div>
    );
  }

  const handleDelete = () => {
    if (!window.confirm(`Supprimer le dossier ${patient.fileNumber} ?`)) return;
    deletePatient(patient.id);
    navigate(routes.patients);
  };

  return (
    <div>
      <PageHeader
        title={patientFullName(patient)}
        description={`Dossier ${patient.fileNumber}`}
        actions={
          <div className="d-flex gap-2 flex-wrap ph-page-header-actions">
            <Link to={routes.patients} className="btn btn-outline-secondary btn-sm">
              Liste
            </Link>
            {canWrite && (
              <button
                type="button"
                className="btn btn-outline-primary btn-sm"
                onClick={() => setEditOpen(true)}
              >
                Modifier
              </button>
            )}
            <Link
              to={`${routes.pharmacyInvoice}?patientId=${patient.id}`}
              className="btn btn-primary btn-sm"
            >
              Facturer
            </Link>
            <Link
              to={`${routes.patientRecords}?patientId=${patient.id}`}
              className="btn btn-outline-secondary btn-sm"
            >
              Dossier médical
            </Link>
            <Link to={routes.consultations} className="btn btn-outline-primary btn-sm">
              Consultations
            </Link>
          </div>
        }
      />

      <div className="row g-3">
        <div className="col-lg-6">
          <div className="ph-invoice-sheet h-100">
            <div className="ph-invoice-section-title">Identité</div>
            <dl className="row mb-0 small">
              <dt className="col-sm-4 text-muted">N° dossier</dt>
              <dd className="col-sm-8">
                <span className="ph-inv-badge">{patient.fileNumber}</span>
              </dd>
              <dt className="col-sm-4 text-muted">Nom complet</dt>
              <dd className="col-sm-8 fw-medium">{patientFullName(patient)}</dd>
              <dt className="col-sm-4 text-muted">Genre</dt>
              <dd className="col-sm-8">{patientGenderLabel(patient.gender)}</dd>
              <dt className="col-sm-4 text-muted">Type patient</dt>
              <dd className="col-sm-8">{patientTypeLabel(patient.patientType)}</dd>
              <dt className="col-sm-4 text-muted">Téléphone</dt>
              <dd className="col-sm-8">{patient.phone || "—"}</dd>
              <dt className="col-sm-4 text-muted">Adresse</dt>
              <dd className="col-sm-8">{patient.address || "—"}</dd>
              <dt className="col-sm-4 text-muted">Naissance</dt>
              <dd className="col-sm-8">
                {patient.birthDate
                  ? new Date(patient.birthDate).toLocaleDateString("fr-FR")
                  : "—"}
              </dd>
              {patient.notes && (
                <>
                  <dt className="col-sm-4 text-muted">Notes</dt>
                  <dd className="col-sm-8">{patient.notes}</dd>
                </>
              )}
            </dl>
          </div>
        </div>

        <div className="col-lg-6">
          <div className="ph-invoice-sheet h-100">
            <div className="d-flex justify-content-between align-items-center mb-2">
              <div className="ph-invoice-section-title mb-0">Consultations</div>
              <button
                type="button"
                className="btn btn-link btn-sm p-0"
                onClick={() => {
                  const created = createConsultation({
                    patientId: patient.id,
                    motif: "Consultation",
                  });
                  navigate(consultationDetailPath(created.id));
                }}
              >
                + Nouvelle
              </button>
            </div>
            {consultations.length === 0 ? (
              <p className="text-muted small mb-0">Aucune consultation enregistrée.</p>
            ) : (
              <ul className="list-unstyled mb-0 small">
                {consultations.slice(0, 5).map((c) => (
                  <li key={c.id} className="mb-2 pb-2 border-bottom">
                    <Link to={consultationDetailPath(c.id)} className="fw-medium">
                      {c.motif || "Consultation"}
                    </Link>
                    <div className="text-muted">
                      {formatInvoiceDateShort(c.createdAt)} ·{" "}
                      <span className={`hosp-clinical-badge hosp-clinical-badge--${c.status}`}>
                        {CONSULTATION_STATUS_LABELS[c.status]}
                      </span>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>

      <div className="ph-invoice-sheet mt-3">
        <div className="ph-invoice-section-title">Achats pharmacie</div>
        {invoices.length === 0 ? (
          <p className="text-muted small mb-0">Aucune facture pharmacie pour ce patient.</p>
        ) : (
          <div className="table-responsive ph-diag-table">
            <table className="table table-sm ph-dash-table mb-0">
              <thead>
                <tr>
                  <th>N° facture</th>
                  <th>Date</th>
                  <th className="text-end">Montant</th>
                </tr>
              </thead>
              <tbody>
                {invoices.map((inv) => (
                  <tr key={inv.id} className="ph-diag-row">
                    <td data-label="N° facture">
                      <span className="ph-inv-badge">{inv.number}</span>
                    </td>
                    <td data-label="Date" className="small">
                      {formatInvoiceDateShort(inv.validatedAt)}
                    </td>
                    <td data-label="Montant" className="text-end fw-semibold">
                      {formatMoney(inv.subtotal)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {canWrite && (
        <div className="mt-3">
          <button type="button" className="btn btn-link btn-sm text-danger p-0" onClick={handleDelete}>
            Supprimer ce patient
          </button>
        </div>
      )}

      <PatientFormModal
        open={editOpen && canWrite}
        patient={patient}
        onClose={() => setEditOpen(false)}
        onSave={(input) => {
          updatePatient(patient.id, input);
          setRefreshKey((k) => k + 1);
        }}
      />
    </div>
  );
}
